import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import _ from 'underscore';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	Chat,
	IconButton,
	StarsIcon,
	Tooltip,
	WorkingArea,
	chatSlideIn,
} from '@treams/treams-ui';

const Container = styled(Box)`
	background: ${(props) => props.theme.tokens.surface.white};
	border-radius: ${(props) => props.theme.tokens.radius.md}px;
`;

const DelayedBubble = styled(Chat.Bubble)<{ $index: number }>`
	animation: 1s cubic-bezier(0, 1, 0, 1) ${chatSlideIn};
	animation-delay: ${(props) => props.$index * 1000}ms;
	animation-fill-mode: backwards;
`;

function ListItem(props: { text: string }) {
	return (
		<Flex alignItems="baseline">
			<Box width={20} flexGrow={0} flexShrink={0} mr={1} textAlign="right">
				{props.text.substring(0, props.text.indexOf('.'))}.
			</Box>
			<Box flexGrow={1} flexShrink={1}>
				{props.text.substring(props.text.indexOf('.') + 1)}
			</Box>
		</Flex>
	);
}

function AIChat(props: { name: string; context: Record<string, string> }) {
	const { t } = useTranslation();

	const [context, setContext] = useState(props.context);
	const { language } = useLocalize();

	const { data } = graphql.usePromptFeedbackTipsQuery({
		variables: {
			input: {
				answer: context.answer,
				question: context.question,
				locale: language.id,
			},
		},
	});

	return (
		<Box mt={2}>
			<Flex alignItems="top">
				<Box mr={2} mt="6px" flexGrow={0} flexShrink={0}>
					<Avatar
						user={
							{
								firstName: t('ai.identity.firstName'),
								lastName: t('ai.identity.lastName'),
							} as graphql.User
						}
						size={24}
					/>
				</Box>
				<Box>
					{data ? (
						data.promptFeedbackTips.messages.map((message, i) => (
							<Flex mt={i === 0 ? 0 : 1} key={i}>
								<DelayedBubble type="ai" p={2} $index={i}>
									{message.text.match(/^[0-9+]\./) ? (
										<ListItem text={message.text} />
									) : (
										message.text
									)}
								</DelayedBubble>
							</Flex>
						))
					) : (
						<Box>
							<Flex>
								<DelayedBubble type="ai" p={2} $index={1}>
									{t('ai.thinking')}
								</DelayedBubble>
							</Flex>
							<Flex mt={1}>
								<Chat.Typing />
							</Flex>
						</Box>
					)}
					{!_.isEqual(props.context, context) && (
						<Flex mt={1}>
							<Flex flexGrow={1} flexShrink={1} mr={2}>
								<DelayedBubble type="ai" p={2} $index={0}>
									{t('ai.changed.message')}
								</DelayedBubble>
							</Flex>
							<Button
								type="primary"
								small
								title={t('ai.changed.button')}
								onClick={() => setContext(props.context)}
							/>
						</Flex>
					)}
				</Box>
			</Flex>
		</Box>
	);
}

export function FeedbackSuggestionsAI(
	props: PropsWithChildren<{
		name: string;
		context: Record<string, string>;
		tooltip: string;
		offsetTop: number;
	}>,
) {
	const [active, setActive] = useState(false);

	return (
		<Box style={{ position: 'relative' }}>
			<Box style={{ position: 'absolute', left: -40, top: props.offsetTop }}>
				<Tooltip content={props.tooltip} position="right" portal>
					<IconButton type="secondary" icon={<StarsIcon />} onClick={() => setActive(true)} />
				</Tooltip>
			</Box>
			<div style={{ height: 1 }} />
			<Box mb={2} mx={-2} mt="-9px">
				<WorkingArea active={active} onDismiss={() => setActive(false)}>
					{(active) => (
						<Container p={2} px={2}>
							{props.children}
							{active && <AIChat name={props.name} context={props.context} />}
						</Container>
					)}
				</WorkingArea>
			</Box>
		</Box>
	);
}
